import React, { useEffect, useRef, useState } from 'react';
import { Head, Link, router, usePage } from '@inertiajs/react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Workspace.css';

/**
 * The script's contract — the first conversation, not a form.
 *
 * Walked one facet at a time (premise → theme → philosophical conflict → genre promise → tone →
 * world rules → arcs). For each, the model proposes the READINGS THE SCRIPT SUPPORTS — seeded from the
 * analysis on file, reconciled against the scenes, one recommended — and the writer CHOOSES. A single
 * reading invites assent; a choice between readings the script actually supports makes the writer find
 * their intent against the page. "I'm not sure" is a first-class answer: it opens the conversation
 * with the one question that settles it. "Something else" is the writer's authority: the row lands in
 * their words and the model reads it against the scenes.
 */

const post = (url, data, opts = {}) => router.post(url, data, { preserveScroll: true, ...opts });

/* ───────────────────────────── a candidate's body: labelled lines ───────────────────────────── */

const LINES = ['Reading', 'Where it lives', 'Differs from', 'If this, then', 'Against it'];

function parseCandidate(body) {
  const out = [];
  (body || '').split('\n').forEach((raw) => {
    const line = raw.trim();
    if (!line) return;
    const m = line.match(/^([A-Za-z ,]+?)\s*:\s*(.*)$/);
    const label = m ? LINES.find((l) => m[1].toLowerCase().startsWith(l.toLowerCase())) : null;
    if (label) out.push({ label, head: m[1].trim(), text: m[2].trim() });
    else if (out.length) out[out.length - 1].text += `\n${line}`;
    else out.push({ label: 'Reading', head: 'Reading', text: line });
  });
  return out;
}

function CandidateBody({ body, compact = false }) {
  const parts = parseCandidate(body);
  if (parts.length <= 1 && parts[0]?.label === 'Reading') return <div className={compact ? 'srs-body' : 'srs-rung-claim'}>{parts[0]?.text || body}</div>;
  return (
    <div className="srs-sections">
      {parts.map((p, i) => (
        <div key={i} className={`srs-section ${p.label === 'Reading' ? 'lead' : p.label === 'Against it' ? 'costs' : p.label === 'If this, then' ? 'mechanism' : ''}`}>
          {p.label !== 'Reading' && <span className="srs-section-head">{p.head}: </span>}
          <span className="srs-section-text">{p.text}</span>
        </div>
      ))}
    </div>
  );
}

/* ───────────────────────────── one candidate ───────────────────────────── */

function Candidate({ row, uuid, single, busy, setBusy, onDiscuss }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(row.title || '');
  const [body, setBody] = useState(row.body || '');
  const isBusy = busy === row.id;
  const provenance = /^\s*Reading:\s*From the analysis/i.test(row.body || '') ? 'from your analysis' : 'read from the scenes';

  const act = (path, data = {}, onSuccess) => {
    setBusy(row.id);
    post(`/srs/p/${uuid}/a/${row.id}/${path}`, data, { onSuccess, onFinish: () => setBusy(null) });
  };
  const choose = () => {
    const data = editing ? { title, body } : {};
    if (single) data.exclusive = true;
    act('confirm', data, () => { setEditing(false); toast.success(single ? `Chosen: ${row.title || row.facet}` : `Kept: ${row.title || row.facet}`, { autoClose: 2000 }); });
  };

  return (
    <div className={`srs-card candidate proposed${row.recommended ? ' recommended' : ''}`}>
      {row.recommended && <div className="srs-reco">★ Recommended{row.rationale ? <span className="srs-reco-why"> — {row.rationale}</span> : null}</div>}
      <div className="srs-card-stack">
        <span className="srs-facet-head">
          {row.title && <span className="srs-facet-label">{row.title}</span>}
          <span className="srs-spec">{provenance}</span>
        </span>
        {editing ? (
          <>
            <input className="form-control form-control-sm mb-1" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Handle" aria-label="Handle" />
            <textarea className="form-control form-control-sm srs-input" value={body} onChange={(e) => setBody(e.target.value)} aria-label="The commitment" style={{ minHeight: '9rem' }} />
          </>
        ) : (
          <CandidateBody body={row.body} />
        )}
        {row.anchored_to && <div className="srs-anchor">scenes: {row.anchored_to}</div>}
      </div>
      <div className="d-flex flex-wrap gap-2 mt-2 align-items-center">
        {isBusy && <span className="spinner-border spinner-border-sm text-secondary" role="status" aria-label="working" />}
        {!isBusy && (
          <>
            <button className="btn btn-sm btn-primary" onClick={choose}>{editing ? (single ? 'Save — this one' : 'Save — keep it') : (single ? 'This one' : 'Keep')}</button>
            {editing
              ? <button className="btn btn-sm btn-outline-secondary" onClick={() => { setEditing(false); setTitle(row.title || ''); setBody(row.body || ''); }}>Cancel</button>
              : <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditing(true)}>Edit, then {single ? 'this one' : 'keep'}</button>}
            <button className="btn btn-sm btn-outline-secondary" onClick={() => onDiscuss(row)}>Discuss</button>
            {!single && <button className="btn btn-sm btn-link text-danger ms-auto" onClick={() => act('cancel')}>Drop</button>}
          </>
        )}
      </div>
    </div>
  );
}

/* ───────────────────────────── a chosen row (done facet) ───────────────────────────── */

function Chosen({ row, uuid, busy, setBusy, onDiscuss }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(row.title || '');
  const [body, setBody] = useState(row.body || '');
  const isBusy = busy === row.id;
  const act = (path, data = {}, onSuccess) => {
    setBusy(row.id);
    post(`/srs/p/${uuid}/a/${row.id}/${path}`, data, { onSuccess, onFinish: () => setBusy(null) });
  };
  const yours = !row.model_authored;
  return (
    <div className="srs-card compact">
      <div className="srs-card-stack">
        <span className="srs-facet-head">
          {row.title && <span className="srs-facet-label">{row.title}</span>}
          <span className="srs-spec">{row.status === 'locked' ? (yours ? 'stated by you' : 'chosen by you') : 'drafted — not yet chosen'}</span>
        </span>
        {editing ? (
          <>
            <input className="form-control form-control-sm mb-1" value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Handle" />
            <textarea className="form-control form-control-sm srs-input" value={body} onChange={(e) => setBody(e.target.value)} aria-label="The commitment" style={{ minHeight: '8rem' }} />
          </>
        ) : <CandidateBody body={row.body} compact />}
        {row.anchored_to && <div className="srs-anchor">scenes: {row.anchored_to}</div>}
      </div>
      <div className="d-flex flex-wrap gap-2 mt-2 align-items-center">
        {isBusy && <span className="spinner-border spinner-border-sm text-secondary" role="status" aria-label="working" />}
        {!isBusy && editing && (
          <>
            <button className="btn btn-sm btn-primary" onClick={() => act(row.status === 'locked' ? 'update' : 'confirm', { title, body }, () => setEditing(false))}>{row.status === 'locked' ? 'Save as new version' : 'Save & choose'}</button>
            <button className="btn btn-sm btn-outline-secondary" onClick={() => { setEditing(false); setTitle(row.title || ''); setBody(row.body || ''); }}>Cancel</button>
          </>
        )}
        {!isBusy && !editing && (
          <>
            {row.status !== 'locked' && <button className="btn btn-sm btn-primary" onClick={() => act('confirm')}>Choose</button>}
            <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditing(true)}>Fix the wording</button>
            <button className="btn btn-sm btn-outline-secondary" onClick={() => onDiscuss(row)}>Discuss</button>
            <button className="btn btn-sm btn-link text-danger ms-auto" onClick={() => act('cancel')}>Retire</button>
          </>
        )}
      </div>
    </div>
  );
}

/* ───────────────────────────── "something else" — the writer's own ───────────────────────────── */

function Yours({ facet, uuid, unitId, onDone }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSaving(true);
    post(`/srs/p/${uuid}/u/${unitId}/artifacts`, { type: 'story_constraint', facet: facet.facet, title, body, return: 'contract', check: true }, {
      onSuccess: () => { setTitle(''); setBody(''); setOpen(false); onDone?.(); },
      onFinish: () => setSaving(false),
    });
  };
  if (!open) return <button className="btn btn-outline-primary" onClick={() => setOpen(true)}>Something else — mine</button>;
  return (
    <form className="srs-card w-100" onSubmit={submit}>
      <div className="srs-meta mb-2">In your words. It lands as yours; Luna then reads it against the scenes and says where they agree and where they argue.</div>
      <input className="form-control form-control-sm mb-1" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={facet.facet === 'arc' ? 'Character' : 'A handle, a few words'} aria-label="Handle" />
      <textarea className="form-control form-control-sm srs-input" value={body} onChange={(e) => setBody(e.target.value)} placeholder={facet.question} aria-label="Your statement" required />
      <div className="d-flex justify-content-end gap-2 mt-2">
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setOpen(false)}>Cancel</button>
        <button type="submit" className="btn btn-sm btn-primary" disabled={saving || !body.trim()}>{saving ? 'Saving and reading…' : "That's mine — read it against the scenes"}</button>
      </div>
    </form>
  );
}

/* ───────────────────────────── the walk ───────────────────────────── */

function FacetDone({ f, index, uuid, busy, setBusy, onDiscuss, onReconsider, working }) {
  const [open, setOpen] = useState(false);
  const first = f.chosen[0];
  const handle = f.single ? (first?.title || (parseCandidate(first?.body)[0]?.text || '').slice(0, 90)) : `${f.chosen.length} kept`;
  return (
    <div className={`srs-rung done${open ? ' open' : ''}`}>
      <div className="d-flex flex-wrap align-items-baseline gap-2">
        <span className="srs-rung-tick" aria-hidden="true">✓</span>
        <span className="srs-path-no">{index}</span>
        <span className="srs-facet-label">{f.label}</span>
        <span className="srs-rung-handle">{handle}</span>
        <button type="button" className="btn btn-sm btn-link srs-rung-change" onClick={() => setOpen((v) => !v)}>{open ? 'hide' : 'show'}</button>
      </div>
      {open && (
        <div className="mt-2">
          {f.chosen.map((r) => <Chosen key={r.id} row={r} uuid={uuid} busy={busy} setBusy={setBusy} onDiscuss={onDiscuss} />)}
          {f.set_aside.length > 0 && (
            <details className="srs-details mt-2">
              <summary className="srs-label">Set aside · {f.set_aside.length}</summary>
              {f.set_aside.map((r) => (
                <div key={r.id} className="srs-card compact">
                  {r.title && <div className="fw-semibold">{r.title}</div>}
                  <CandidateBody body={r.body} compact />
                </div>
              ))}
            </details>
          )}
          <div className="mt-2 d-flex gap-2">
            <button className="btn btn-sm btn-outline-secondary" disabled={working} onClick={() => onReconsider(f.facet)}>{working ? 'Working…' : 'Reconsider — show me other readings'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function FacetActive({ f, index, total, uuid, unitId, busy, setBusy, onDiscuss, onUnsure, working, inFlight, onRetry }) {
  const rec = f.candidates.filter((r) => r.recommended).map((r) => r.id);
  const rest = f.candidates.map((r) => r.id);
  const keepMany = (ids) => {
    if (!ids.length) return;
    setBusy('many');
    post(`/srs/p/${uuid}/u/${unitId}/accept`, { ids }, { onFinish: () => setBusy(null) });
  };
  const dropRest = () => {
    if (!rest.length) return;
    setBusy('many');
    post(`/srs/p/${uuid}/u/${unitId}/cancel-many`, { ids: rest }, { onFinish: () => setBusy(null) });
  };

  return (
    <section className="srs-now">
      <div className="srs-now-head">
        <div className="srs-step-no">{index} of {total} · {f.label}</div>
        <h2 className="srs-now-title">{f.question}<span className="srs-spec">{f.label}</span></h2>
        <div className="srs-explain"><div><span className="srs-explain-k">Why it matters</span>{f.why}</div></div>
      </div>

      {(working || inFlight) && (
        <div className="d-flex align-items-center gap-3 mt-3">
          <span className="spinner-border text-primary" role="status" aria-label="reading" />
          <span className="text-muted">Reading the whole script for the {f.label.toLowerCase()}… usually 30–90 seconds.{inFlight && !working ? ' Still running from before — this page refreshes itself.' : ''}</span>
        </div>
      )}

      {!working && !inFlight && f.failure && f.candidates.length === 0 && (
        <div className="mt-3">
          <div className="srs-now-why">The call to the model failed: <code>{f.failure.replace(/^The model call failed:\s*/, '')}</code>. Usually a network blip.</div>
          <button className="btn btn-primary mt-2" onClick={onRetry}>Try again</button>
        </div>
      )}

      {!working && !inFlight && f.read && (
        <details className="srs-details mt-3" open={f.candidates.length <= 1}>
          <summary className="srs-label">What the scenes argue</summary>
          <div className="srs-read-body">{f.read}</div>
          {f.errors?.length > 0 && <div className="srs-meta text-danger mt-1">{f.errors.join(' ')}</div>}
        </details>
      )}

      {!working && !inFlight && f.candidates.length > 0 && (
        <>
          <div className={`mt-3 ${f.single ? 'srs-candidates' : ''}`}>
            {f.candidates.map((r) => <Candidate key={r.id} row={r} uuid={uuid} single={f.single} busy={busy} setBusy={setBusy} onDiscuss={onDiscuss} />)}
          </div>
          {!f.single && (
            <div className="srs-batch d-flex flex-wrap gap-2 mt-3 align-items-center">
              {rec.length > 0 && <button className="btn btn-sm btn-primary" disabled={busy === 'many'} onClick={() => keepMany(rec)}>Keep recommended ({rec.length})</button>}
              <button className="btn btn-sm btn-outline-primary" disabled={busy === 'many'} onClick={() => keepMany(rest)}>Keep all ({rest.length})</button>
              <button className="btn btn-sm btn-link text-danger ms-auto" disabled={busy === 'many'} onClick={dropRest}>Drop the rest</button>
            </div>
          )}
        </>
      )}

      {!working && !inFlight && f.ran && f.candidates.length === 0 && !f.failure && (
        <div className="srs-now-why mt-3">Nothing came back for this facet{f.errors?.length ? `: ${f.errors.join(' ')}` : '.'} State it yourself, or try again.</div>
      )}

      {!working && !inFlight && f.single && f.chosen.length > 0 && f.candidates.length > 0 && (
        <div className="srs-meta mt-3">You already chose <strong>{f.chosen[0].title || 'a reading'}</strong>; these are alternatives. Choosing one replaces it.</div>
      )}

      {!working && !inFlight && (f.ran || f.candidates.length > 0) && (
        <div className="d-flex flex-wrap gap-2 mt-3 align-items-start">
          <Yours facet={f} uuid={uuid} unitId={unitId} />
          {f.single && f.candidates.length > 0 && (
            <button className="btn btn-outline-secondary" onClick={() => onUnsure(f.facet)}>I'm not sure</button>
          )}
          {f.single && f.chosen.length > 0 && f.candidates.length > 0 && (
            <button className="btn btn-outline-secondary" disabled={busy === 'many'} onClick={dropRest}>Keep what I have</button>
          )}
          {f.ran && f.candidates.length === 0 && <button className="btn btn-outline-secondary" onClick={onRetry}>Ask again</button>}
        </div>
      )}
    </section>
  );
}

/* ───────────────────────────── the conversation ───────────────────────────── */

/* The model sometimes bolds with **; render that, nothing else. */
function Plain({ text }) {
  const parts = String(text || '').split(/\*\*/);
  return parts.map((p, i) => (i % 2 ? <strong key={i}>{p}</strong> : <React.Fragment key={i}>{p}</React.Fragment>));
}

function Discuss({ messages, about, setAbout, uuid, unitId, inputRef, nudge }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const shown = messages.filter((m) => !m.meta?.step);
  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [shown.length, sending]);
  const send = (e) => {
    e.preventDefault();
    const msg = text.trim();
    if (!msg || sending) return;
    setSending(true);
    post(`/srs/p/${uuid}/u/${unitId}/messages`, { message: msg, about: about?.id ?? null }, {
      onSuccess: () => setText(''),
      onFinish: () => { setSending(false); inputRef.current?.focus(); },
    });
  };
  const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') send(e); };
  return (
    <section className={`srs-discuss${nudge ? ' pinned' : ''}`}>
      <h2 className="h6 srs-label mb-1">Talk it through</h2>
      {about ? (
        <div className="srs-about">
          <span>about: <strong>{about.title || 'this reading'}</strong></span>
          <button type="button" className="btn btn-sm btn-link p-0 ms-2" onClick={() => setAbout(null)} aria-label="Stop discussing this row">✕</button>
        </div>
      ) : (
        <div className="small text-muted mb-2">Luna has the whole script. Argue with a reading, ask what a scene is doing, or say what you meant — a reading agreed here lands on the left with the same buttons.</div>
      )}
      <div className="srs-discuss-list" ref={listRef}>
        {shown.map((m) => (
          <div key={m.id} className={`srs-msg ${m.role}`}>
            <div className="srs-who">
              {m.role === 'user' ? 'You' : m.role === 'assistant' ? 'Luna' : 'System'}
              {m.about?.title ? <span className="fw-normal text-muted"> · about "{m.about.title}"</span> : null}
            </div>
            <Plain text={m.content} />
            {m.role === 'assistant' && m.proposals?.items?.filter((p) => !p.withdrew).length > 0 && (
              <div className="srs-proposed-note">Proposed {m.proposals.items.filter((p) => !p.withdrew).length} — on the left.</div>
            )}
            {m.meta?.errors?.length > 0 && <div className="srs-proposed-note text-danger">{m.meta.errors.join(' ')}</div>}
          </div>
        ))}
        {sending && <div className="srs-msg assistant text-muted"><span className="spinner-border spinner-border-sm me-2" role="status" aria-label="thinking" />Thinking…</div>}
      </div>
      <form onSubmit={send} className="mt-2">
        <textarea ref={inputRef} className="form-control srs-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKey}
          placeholder={about ? `Push back on "${about.title || 'this'}", or say what you meant…` : 'Ask, disagree, or say what you meant…'} disabled={sending} />
        <div className="d-flex justify-content-end mt-2"><button type="submit" className="btn btn-outline-primary" disabled={sending || !text.trim()}>Send</button></div>
      </form>
    </section>
  );
}

/* ───────────────────────────── page ───────────────────────────── */

export default function Contract({ project, contract, messages = [] }) {
  const { flash } = usePage().props;
  const [busy, setBusy] = useState(null);
  const [working, setWorking] = useState(false);
  const [about, setAbout] = useState(null);
  const [nudge, setNudge] = useState(false);
  const inputRef = useRef(null);

  const uuid = project.uuid;
  const unitId = contract.root_unit_id;
  const active = contract.facets.find((f) => f.state === 'active');
  const activeIndex = contract.facets.findIndex((f) => f.state === 'active') + 1;
  const inFlight = !working && !!contract.run_in_flight;

  const run = (facet, mode = 'candidates') => {
    if (working) return;
    setWorking(true);
    post(`/srs/p/${uuid}/contract/run`, { facet, mode }, { onFinish: () => setWorking(false) });
  };

  // The active facet runs itself when nothing has been proposed for it yet.
  useEffect(() => {
    if (contract.needs_run && active && !working && !inFlight) run(active.facet);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract.needs_run, active?.facet]);

  // A run started elsewhere (or a reload mid-run): watch for it to land.
  useEffect(() => {
    if (!inFlight) return undefined;
    const id = setInterval(() => router.reload({ preserveScroll: true }), 6000);
    return () => clearInterval(id);
  }, [inFlight]);

  const onDiscuss = (row) => { setAbout({ id: row.id, title: row.title }); setNudge(true); setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 0); };
  const onUnsure = (facet) => {
    if (working) return;
    setWorking(true); setNudge(true);
    post(`/srs/p/${uuid}/contract/run`, { facet, mode: 'unsure' }, { onFinish: () => { setWorking(false); setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 0); } });
  };

  const allDone = contract.done_count === contract.total;
  const showDiscuss = messages.some((m) => !m.meta?.step) || !!active?.candidates?.length || allDone || nudge;

  return (
    <div className="srs srs-work container-fluid py-3">
      <Head title={`Contract · ${project.title || 'Script'}`} />
      {flash?.error && <div className="alert alert-danger py-2">{flash.error}</div>}

      <header className="srs-head">
        <div className="d-flex flex-wrap align-items-center gap-3">
          <Link href="/srs" className="text-muted small">‹ Scripts</Link>
          <span className="text-muted small">{project.title}</span>
          <span className="ms-auto text-muted small">{contract.done_count} of {contract.total} settled</span>
        </div>
        <h1 className="h4 mt-2 mb-1">What every scene inherits <span className="srs-spec">Inherited contract · §3.2</span></h1>
        <p className="text-muted mb-0">
          Seven things every scene will be read against. For each, the readings your script supports — one recommended — and you choose.
          If you're not sure, say so: that's the conversation this screen is for. A scene can send you back here.
        </p>
      </header>

      <div className="row g-4 mt-1">
        <div className={showDiscuss ? 'col-12 col-lg-7' : 'col-12 col-lg-9'}>
          <div className="srs-walk">
            {contract.facets.map((f, i) => (
              f.state === 'done'
                ? <FacetDone key={f.facet} f={f} index={i + 1} uuid={uuid} busy={busy} setBusy={setBusy} onDiscuss={onDiscuss} onReconsider={(facet) => run(facet, 'reconsider')} working={working} />
                : f.state === 'active'
                  ? <FacetActive key={f.facet} f={f} index={i + 1} total={contract.total} uuid={uuid} unitId={unitId} busy={busy} setBusy={setBusy}
                      onDiscuss={onDiscuss} onUnsure={onUnsure} working={working} inFlight={inFlight && contract.run_in_flight === f.facet} onRetry={() => run(f.facet)} />
                  : (
                    <div key={f.facet} className="srs-rung pending">
                      <span className="srs-rung-then">then</span>
                      <span className="srs-path-no">{i + 1}</span>
                      <span className="srs-facet-label">{f.label}</span>
                    </div>
                  )
            ))}
          </div>

          {contract.tensions?.length > 0 && (
            <details className="srs-details mt-3" open>
              <summary className="srs-label">Tensions to carry into the scenes · {contract.tensions.length}</summary>
              <div className="srs-meta mb-2">Where a choice you made and the pages disagree. Each waits at the scenes it names.</div>
              {contract.tensions.map((t) => (
                <div key={t.id} className={`srs-card compact ${t.status}`}>
                  {t.title && <div className="fw-semibold">{t.title}</div>}
                  <div className="srs-body">{t.body}</div>
                  {t.anchored_to && <div className="srs-anchor mt-1">scenes: {t.anchored_to}</div>}
                  {t.status === 'proposed' && (
                    <div className="d-flex gap-2 mt-2">
                      <button className="btn btn-sm btn-primary" disabled={busy === t.id} onClick={() => { setBusy(t.id); post(`/srs/p/${uuid}/a/${t.id}/confirm`, {}, { onFinish: () => setBusy(null) }); }}>Carry it</button>
                      <button className="btn btn-sm btn-link text-danger" disabled={busy === t.id} onClick={() => { setBusy(t.id); post(`/srs/p/${uuid}/a/${t.id}/cancel`, {}, { onFinish: () => setBusy(null) }); }}>Dismiss</button>
                    </div>
                  )}
                </div>
              ))}
            </details>
          )}

          <div className="d-flex flex-wrap align-items-center gap-3 mt-4">
            <Link href={`/srs/p/${uuid}/scenes`} className={`btn ${allDone ? 'btn-primary btn-lg' : 'btn-outline-secondary'}`}>
              {allDone ? 'Continue: choose a scene →' : 'Skip ahead to the scenes →'}
            </Link>
            {!allDone && <span className="srs-meta">Scenes inherit what is settled; unsettled facets are read as provisional and say so.</span>}
          </div>
        </div>

        {showDiscuss && (
          <div className="col-12 col-lg-5">
            <div className="srs-side">
              <Discuss messages={messages} about={about} setAbout={setAbout} uuid={uuid} unitId={unitId} inputRef={inputRef} nudge={nudge} />
            </div>
          </div>
        )}
      </div>

      <ToastContainer position="bottom-right" newestOnTop hideProgressBar />
    </div>
  );
}
